import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { Purchase } from '../../models/purchase.model';
import { PurchaseService } from '../../services/purchase.service';
import { CurrencyFixPipe } from '../../pipes/currency-fix.pipe';
import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ModalComponent } from '../../components/modal/modal';

@Component({
  selector: 'app-checkout',
  standalone: true,
  imports: [CommonModule, FormsModule, CurrencyFixPipe, NgxMaskDirective, ModalComponent],
  providers: [provideNgxMask()],
  templateUrl: './checkout.html',
  styleUrls: ['./checkout.scss']
})
export class CheckoutComponent implements OnInit {
  purchase: Purchase | null = null;
  loading = true;
  error = false;
  paymentMethod: 'credit-card' | 'pix' = 'credit-card';

  // Campos do cartão de crédito
  cardNumber: string = '';
  cardName: string = '';
  cardDocument: string = '';
  cardExpiry: string = '';
  cardCvv: string = '';
  pixCopied: boolean = false;

  // Status de processamento
  processing = false;
  success = false;

  // Modal de erro
  errorModalOpen = false;
  errorModalTitle = 'Erro no Pagamento';
  errorModalMessage = '';

  // QR Code PIX (será preenchido após a API retornar)
  pixQrCode = '';
  pixCode = '';
  pixExpirationDate = '';

  constructor(
    private route: ActivatedRoute,
    private purchaseService: PurchaseService,
    private router: Router
  ) { }

  /**
   * Extrai a mensagem de erro da resposta da API
   * @param error Objeto de erro da API
   * @returns Mensagem de erro extraída ou mensagem padrão
   */
  private extractErrorMessage(error: any): string {
    console.log('Extraindo mensagem de erro:', error);

    // Mensagem padrão de erro
    const defaultErrorMessage = 'Não foi possivel processar o seu pagamento, por favor revise os dados do cartão e tente novamente.';

    try {
      // Se não houver erro, retorna a mensagem padrão
      if (!error) {
        console.log('Erro vazio, usando mensagem padrão');
        return defaultErrorMessage;
      }

      // Formato esperado: { "error": "failed to process card payment: {\"errors\":[{\"code\":\"invalid_creditCard\",\"description\":\"O cartão informado está expirado.\"}]}, status code: 400" }

      // Primeiro, extrai o campo error do objeto de resposta
      let errorString = '';

      if (error.error) {
        // Se for um objeto com campo error
        errorString = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
      } else {
        // Se for o próprio erro
        errorString = typeof error === 'string' ? error : JSON.stringify(error);
      }

      console.log('String de erro para processamento:', errorString);

      // Verifica se a string contém o padrão esperado
      if (errorString.includes('failed to process') && errorString.includes('errors') && errorString.includes('description')) {
        // Extrai diretamente a descrição usando regex
        const descriptionPattern = /"description"\s*:\s*"([^"]+)"/;
        const descriptionMatch = errorString.match(descriptionPattern);

        if (descriptionMatch && descriptionMatch[1]) {
          console.log('Mensagem de erro extraída com regex de description:', descriptionMatch[1]);
          return descriptionMatch[1];
        }

        // Tenta extrair o JSON embutido na string
        try {
          // Procura por um objeto JSON dentro da string
          const jsonPattern = /\{\\"errors\\":\[.*?\]\}/;
          const jsonMatch = errorString.match(jsonPattern);

          if (jsonMatch) {
            // Substitui as barras invertidas escapadas para poder fazer o parse
            const cleanJson = jsonMatch[0].replace(/\\\"/g, '"');
            console.log('JSON limpo para parse:', cleanJson);

            const parsedJson = JSON.parse(cleanJson);
            if (parsedJson.errors && parsedJson.errors.length > 0 && parsedJson.errors[0].description) {
              console.log('Mensagem extraída do JSON embutido:', parsedJson.errors[0].description);
              return parsedJson.errors[0].description;
            }
          }
        } catch (jsonParseError) {
          console.error('Erro ao analisar JSON embutido:', jsonParseError);
        }
      }

      // Tenta outras abordagens se o formato específico não funcionar

      // Verifica se é um objeto com a estrutura errors diretamente
      if (typeof error === 'object' && error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
        if (error.errors[0].description) {
          console.log('Mensagem extraída do objeto de erro direto:', error.errors[0].description);
          return error.errors[0].description;
        }
      }

      // Verifica se error.error é um objeto com a estrutura errors
      if (typeof error === 'object' && error.error && typeof error.error === 'object') {
        if (error.error.errors && Array.isArray(error.error.errors) && error.error.errors.length > 0) {
          if (error.error.errors[0].description) {
            console.log('Mensagem extraída do objeto error.error:', error.error.errors[0].description);
            return error.error.errors[0].description;
          }
        }
      }

      // Se chegou até aqui, usa a mensagem padrão
      console.log('Nenhum formato de erro reconhecido, usando mensagem padrão');
      return defaultErrorMessage;
    } catch (e) {
      console.error('Erro ao processar mensagem de erro:', e);
      return defaultErrorMessage;
    }
  }

  ngOnInit(): void {
    this.route.params.pipe(
      switchMap(params => {
        const id = params['id'];
        if (id) {
          return this.purchaseService.getPurchaseById(id);
        }
        return of(null);
      })
    ).subscribe({
      next: (purchase) => {
        if (purchase) {
          this.purchase = purchase;
          this.loading = false;
        } else {
          this.error = true;
          this.loading = false;
        }
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }

  setPaymentMethod(method: 'credit-card' | 'pix'): void {
    this.paymentMethod = method;
  }

  processPayment(): void {
    this.processing = true;

    if (this.paymentMethod === 'credit-card' && this.purchase?.id) {
      const expMonth = this.cardExpiry.substring(0, 2);
      const expYear = this.cardExpiry.substring(2, 4);

      const paymentData = {
        card_data: {
          number: this.cardNumber.replace(/\s/g, ''),
          cvv: this.cardCvv,
          expiration_month: expMonth,
          expiration_year: `20${expYear}` // Convertendo YY para YYYY
        },
        holder_data: {
          name: this.cardName,
          cpf_cnpj: this.cardDocument.replace(/[^0-9]/g, '') // Remove caracteres não numéricos
        }
      };

      this.purchaseService.processCardPayment(this.purchase.id, paymentData).subscribe({
        next: () => {
          this.processing = false;
          this.success = true;
        },
        error: (error) => {
          this.processing = false;

          // Usa a função centralizada para extrair a mensagem de erro
          this.errorModalMessage = this.extractErrorMessage(error);

          // Exibe o modal de erro
          this.errorModalOpen = true;
          console.log('Modal de erro exibido com mensagem:', this.errorModalMessage);
        }
      });
    } else if (this.paymentMethod === 'pix' && this.purchase?.id) {
      // Buscar dados PIX da API
      this.purchaseService.getPixData(this.purchase.id).subscribe({
        next: (pixData) => {
          this.processing = false;
          this.success = true;
          // Usar a imagem base64 diretamente
          this.pixQrCode = `data:image/png;base64,${pixData.EncodedImage}`;
          this.pixCode = pixData.Payload;
          this.pixExpirationDate = pixData.ExpirationDate;
        },
        error: (error) => {
          this.processing = false;
          console.error('Erro ao buscar dados PIX:', error);

          // Usa a função centralizada para extrair a mensagem de erro
          this.errorModalMessage = this.extractErrorMessage(error);

          // Exibe o modal de erro
          this.errorModalOpen = true;
          console.log('Modal de erro PIX exibido com mensagem:', this.errorModalMessage);
        }
      });
    } else {
      this.processing = false;
      this.errorModalMessage = 'Erro ao processar pagamento: dados inválidos.';
      this.errorModalOpen = true;
    }
  }

  copyPixCode(): void {
    const pixCode = this.pixCode;
    navigator.clipboard.writeText(pixCode)
      .then(() => {
        this.pixCopied = true;
        // Reset feedback after 3 seconds
        setTimeout(() => {
          this.pixCopied = false;
        }, 3000);
      })
      .catch(err => {
        console.error('Erro ao copiar código PIX:', err);
      });
  }

  navigateToPurchases(): void {
    this.router.navigate(['/purchases']);
  }

  closeErrorModal(): void {
    this.errorModalOpen = false;
  }
}
